import React from 'react';
import styled from '@emotion/styled';

type ButtonVariant = 'primary' | 'secondary' | 'danger';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
}

const variantTokens: Record<
  ButtonVariant,
  { background: string; color: string; boxShadow: string; hover: string }
> = {
  primary: {
    background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
    color: '#ffffff',
    boxShadow: '0 15px 35px rgba(79, 70, 229, 0.35)',
    hover: 'linear-gradient(135deg, #4338ca, #4c1d95)',
  },
  secondary: {
    background: '#eef2ff',
    color: '#312e81',
    boxShadow: '0 10px 25px rgba(79, 70, 229, 0.15)',
    hover: '#e0e7ff',
  },
  danger: {
    background: '#ef4444',
    color: '#ffffff',
    boxShadow: '0 12px 30px rgba(239, 68, 68, 0.35)',
    hover: '#dc2626',
  },
};

const sizeTokens: Record<ButtonSize, { padding: string; fontSize: string }> = {
  small: { padding: '0.6rem 1.2rem', fontSize: '0.9rem' },
  medium: { padding: '0.95rem 1.2rem', fontSize: '1rem' },
  large: { padding: '1.1rem 1.4rem', fontSize: '1.05rem' },
};

const StyledButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'size',
})<{ variant: ButtonVariant; size: ButtonSize }>`
  width: 100%;
  border: none;
  outline: none;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease;
  color: ${({ variant }) => variantTokens[variant].color};
  background: ${({ variant }) => variantTokens[variant].background};
  box-shadow: ${({ variant }) => variantTokens[variant].boxShadow};
  padding: ${({ size }) => sizeTokens[size].padding};
  font-size: ${({ size }) => sizeTokens[size].fontSize};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: ${({ variant }) => variantTokens[variant].hover};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.35);
  }
`;

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  ...rest
}) => {
  return (
    <StyledButton variant={variant} size={size} {...rest}>
      {children}
    </StyledButton>
  );
};

export default Button;
